import { ref } from 'vue'

/**
 * ADR-11：access_token **只存内存**（刷新页面即失效，靠 Cookie 中的 refresh_token 静默续期）。
 *
 * 令牌本体放在本模块的模块级 ref，而不是 Pinia：
 * 请求层需要读写令牌，Pinia store 又要调用请求层，若令牌挂在 store 上会形成循环依赖。
 * `stores/user.ts` 只是把这里的状态以只读方式暴露给组件。
 */
const accessToken = ref<string | null>(null)
const expiresAt = ref(0)
const permVersion = ref<string | null>(null)
/**
 * 令牌里带回来的当前应用标识（`client_id`，不是数字主键）。
 *
 * 它随令牌走而不是随 localStorage 走：账号切换后旧的应用标识若还留在本地，
 * 菜单接口会按"上一个账号能看到的应用"去查，轻则 403，重则串到别人的应用。
 *
 * 存的是 `client_id` 而非 `uc_app.id`：后者是自增数字，任何人改一个数字就能挨个试过去；
 * 业务标识既不可枚举，也让 URL / 请求头里不再出现内部主键。
 */
const clientId = ref<string | null>(null)

/** 时钟漂移容忍：提前 30s 视为过期 */
const SKEW_MS = 30_000

export const tokenState = { accessToken, expiresAt, permVersion, clientId }

export function setAccessToken(token: string, expiresIn: number, version?: string | null,
  appClientId?: string | null): void {
  accessToken.value = token
  expiresAt.value = Date.now() + Math.max(expiresIn - 5, 1) * 1000
  if (version !== undefined) {
    permVersion.value = version
  }
  if (appClientId !== undefined) {
    clientId.value = appClientId
  }
}

/**
 * 从 access_token 自身还原几个随令牌下发的上下文字段。
 *
 * 授权码链路（{@code /oauth2/token}）的响应只有标准的 {@code access_token / expires_in}，
 * 而菜单与权限要知道"这次登录落在哪个应用"；令牌是自包含的，直接读它比再发一轮请求更可靠 ——
 * 少一次往返，也不会出现"令牌已经是新应用的、刚查回来的却是旧的"这种竞态。
 */
export function readTokenClaims(payload: string): { permVersion: string | null; clientId: string | null } {
  try {
    const body = payload.split('.')[1]
    if (!body) {
      return { permVersion: null, clientId: null }
    }
    const claims: Record<string, unknown> = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')))
    const version = claims['permVersion']
    const app = claims['client_id']
    return {
      permVersion: version === undefined || version === null ? null : String(version),
      clientId: app === undefined || app === null ? null : String(app),
    }
  } catch {
    // 令牌格式异常不该让登录流程中断：退化为"没有额外上下文"，最多是首帧菜单晚一点
    return { permVersion: null, clientId: null }
  }
}

export function clearAccessToken(): void {
  accessToken.value = null
  expiresAt.value = 0
  clientId.value = null
}

export function isAccessTokenUsable(): boolean {
  return accessToken.value !== null && Date.now() < expiresAt.value - SKEW_MS
}
